import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from './button';

interface ToggleGroupContextValue {
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  size?: 'sm' | 'default';
}

const ToggleGroupContext = React.createContext<ToggleGroupContextValue | null>(null);

interface ToggleGroupProps {
  type: 'single';
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  size?: 'sm' | 'default';
  children: React.ReactNode;
  className?: string;
}

/**
 * Toggle group component for single selection between options.
 */
export function ToggleGroup({
  value,
  onValueChange,
  disabled,
  size = 'default',
  children,
  className,
}: ToggleGroupProps) {
  return (
    <ToggleGroupContext.Provider value={{ value, onValueChange, disabled, size }}>
      <div className={cn('inline-flex rounded-md border', className)} role="group">
        {children}
      </div>
    </ToggleGroupContext.Provider>
  );
}

interface ToggleGroupItemProps {
  value: string;
  'aria-label': string;
  children: React.ReactNode;
  className?: string;
}

/**
 * Individual toggle item within a ToggleGroup.
 */
export function ToggleGroupItem({
  value,
  'aria-label': ariaLabel,
  children,
  className,
}: ToggleGroupItemProps) {
  const context = React.useContext(ToggleGroupContext);
  if (!context) {
    throw new Error('ToggleGroupItem must be used within ToggleGroup');
  }

  const isSelected = context.value === value;

  return (
    <Button
      type="button"
      variant={isSelected ? 'default' : 'ghost'}
      size={context.size}
      disabled={context.disabled}
      aria-label={ariaLabel}
      aria-pressed={isSelected}
      onClick={() => context.onValueChange(value)}
      className={cn(
        'rounded-none first:rounded-l-md last:rounded-r-md',
        !isSelected && 'border-0',
        className
      )}
    >
      {children}
    </Button>
  );
}
